import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Stack } from 'react-bootstrap';
import SocialPost from 'components/SocialPost';
import { useGetPostQuery } from 'services';
import { dataLimits } from 'constants/index';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SocialPostSkeletonList } from 'components/Common/Skeleton/SocialPostSkeleton';
import useTranslate from 'hooks/useTranslate';
import useAuthStore from 'store/auth';
import '../styles.scss';
import useFeedsForYouStore, { setMyPostMeta, setMyPostCurrentCursor, setMyPostLastCursor, setForYouMyFeeds } from 'store/feedsForYou';

// Memoized SocialPost component to prevent unnecessary re-renders
const MemoizedSocialPost = React.memo(SocialPost);

const MyFeeds: React.FC = () => {
	const { translate } = useTranslate();
	// const [myFeeds, setMyFeeds] = useState<any[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	const {
		myPostMeta,
		myPostCurrentCursor,
		myPostLastCursor,
		myFeeds,
	}: Record<string, any> = useFeedsForYouStore((state) => state);

	// Memoize search string to prevent unnecessary API calls
	// const searchString = useMemo(
	// 	() =>
	// 		`order=DESC&page=${currentPage}&limit=${dataLimits?.feedsListing}&socialPostType=my-posts`,
	// 	[currentPage],
	// );

	const searchString = `order=DESC&cursor=${myPostCurrentCursor || 0}&limit=${dataLimits?.feedsListing}&socialPostType=my-posts`;

	const {
		data: { docs: postList = {}, meta = {} } = {},
		status,
		refetch,
		isLoading: isQueryLoading,
	} = useGetPostQuery({
		searchString: searchString,
		// isFetch: currentPage,
		isFetch: myPostLastCursor === myPostCurrentCursor ? false : true,
		userToken: isAuthenticated,
	});

	useEffect(() => {
		// Reset state when component mounts
		setForYouMyFeeds([]);
		setCurrentPage(1);
		setIsLoading(false);
		setMyPostMeta({});
		setMyPostCurrentCursor(0);
		setMyPostLastCursor(1);
	}, []);

	// Memoize loadMoreFunc to prevent unnecessary re-renders
	const loadMoreFunc = useCallback(() => {
		if (!isLoading && myPostMeta?.hasMore) {
			setIsLoading(true);
			setMyPostLastCursor(0);
		}
	}, [isLoading, myPostMeta?.hasMore]);

	// const loadMoreFunc = useCallback(() => {
	// 	console.log('🚀 ~ file: index.tsx:120 ~ loadMoreFunc ~ meta:', meta);
	// 		setMyPostLastCursor(0);
	// 	}, [myFeeds]);

	// Memoize refreshOnDeletePost to prevent unnecessary re-renders
	const refreshOnDeletePost = useCallback(() => {
		refetch()
			.then((res: any) => {
				if (res?.data?.docs?.results) {
					setForYouMyFeeds(res.data.docs.results);
				}
			})
			.catch(() => {});
	}, [refetch]);

	// Optimized state update using functional update
	// useEffect(() => {
	// 	if (
	// 		status === 'success' &&
	// 		postList &&
	// 		Array.isArray(postList.results) &&
	// 		postList.results.length > 0 &&
	// 		isStale
	// 	) {
	// 		setMyFeeds((prevFeeds) => {
	// 			// Use Set to avoid duplicates
	// 			const existingIds = new Set(prevFeeds.map((feed: any) => feed.postId));
	// 			const newFeeds = postList.results.filter((feed: any) => !existingIds.has(feed.postId));
	// 			return [...prevFeeds, ...newFeeds];
	// 		});
	// 		setIsLoading(false);
	// 	}
	// }, [postList, status, isStale]);

	useEffect(() => {
		if (
			status === 'success' &&
			postList &&
			Array.isArray(postList.results) &&
			postList.results.length > 0
		) {

			setForYouMyFeeds([...myFeeds, ...postList.results])
			console.log('🚀 ~ file: index.tsx:122 ~ useEffect ~ postList.results:', meta);
			setMyPostMeta(meta);
			setMyPostCurrentCursor(meta?.nextCursor || 0);
			setMyPostLastCursor(meta?.nextCursor || 0);
			setIsLoading(false); // allow another loadMore call after fetch completes
		}
	}, [postList, status, meta]);

	useEffect(() => {
		if (!isQueryLoading) {
			setIsLoading(false);
		}
	}, [isQueryLoading]);

	// Memoize skeleton loader component
	const skeletonLoader = useMemo(
		() => myFeeds.length >= 1 && <SocialPostSkeletonList count={3} noButton={true} />,
		[myFeeds.length],
	);

	// Memoize pull down content
	const pullDownContent = useMemo(
		() => <h3 style={{ textAlign: 'center' }}>&#8595; {translate('Common.pullDownToRefresh')}</h3>,
		[translate],
	);

	// Memoize release content
	const releaseContent = useMemo(
		() => <h3 style={{ textAlign: 'center' }}>&#8593; {translate('Common.releaseToRefresh')}</h3>,
		[translate],
	);

	// Memoize posts rendering
	const renderedPosts = useMemo(
		() =>
			myFeeds.map((post: any, index: number) => (
				<MemoizedSocialPost
					post={post}
					key={`${post?.postId}-${post?.userId}-${index}`}
					refetch={refreshOnDeletePost}
					type={'myfeeds'}
				/>
			)),
		[myFeeds, refreshOnDeletePost],
	);

	// Memoize no posts found message
	const noPostsMessage = useMemo(
		() => (
			<Stack className="fw-bold">
				<p className="mb-0">{translate('Common.noPostFound')}</p>
			</Stack>
		),
		[translate],
	);

	return (
		<div className="social-feed">
			<div className="social-feed-left">
				<Stack className="social-feed-posts">
					<div id="infiniteDiv-social-feeds">
						{myFeeds?.length > 0 ? (
							<InfiniteScroll
								dataLength={myFeeds.length}
								next={loadMoreFunc}
								hasMore={myPostMeta?.hasMore}
								loader={skeletonLoader}
								scrollThreshold="200px"
								refreshFunction={loadMoreFunc}
								pullDownToRefreshThreshold={50}
								pullDownToRefreshContent={pullDownContent}
								releaseToRefreshContent={releaseContent}
								// Performance optimizations
								endMessage={<SocialPostSkeletonList count={3} noButton={true} />}
								style={{ overflow: 'visible' }}
							>
								{myFeeds.length > 0 ? renderedPosts : noPostsMessage}
							</InfiniteScroll>
						) : (
							<div className="white-box bg-white">
								{isLoading || isQueryLoading ? (
									<SocialPostSkeletonList count={3} noButton={true} />
								) : (
									noPostsMessage
								)}
							</div>
						)}
					</div>
				</Stack>
			</div>
		</div>
	);
};

export default React.memo(MyFeeds);
